/**
 * Procedural pixel-art skill icons (24 x 24 logical pixels, shown scaled with pixelated
 * rendering). Every icon = element background + a glyph drawn with a tiny pixel API + an
 * automatic dark outline so it reads at small sizes. All original art, nothing to license.
 */
import {
  cross,
  dagger,
  ELEMENT,
  flame,
  N,
  type Palette,
  Pix,
  renderIcon,
  shieldShape,
  star,
  sword,
} from "./pixelArt";

// ------------------------------------------------------------------ icons

type Recipe = { element: keyof typeof ELEMENT; draw: (p: Pix, pal: Palette) => void };

/** A running boot with speed lines (run skills). */
function boot(p: Pix, c: Palette, wings: boolean) {
  for (const y of [8, 12, 16]) p.line(2, y, 7, y, c.light);
  p.rect(11, 5, 5, 9, c.main);
  p.rect(11, 14, 9, 4, c.main);
  p.rect(11, 17, 9, 1, c.dark);
  p.rect(12, 6, 1, 7, c.light);
  if (wings) {
    p.line(16, 7, 21, 3, c.light, 2);
    p.line(16, 9, 21, 7, c.light);
  }
}

const RECIPES: Record<string, Recipe> = {
  warrior_sprint: { element: "shield", draw: (p, c) => boot(p, c, false) },
  mage_sprint: { element: "frost", draw: (p, c) => boot(p, c, false) },
  priest_sprint: { element: "holy", draw: (p, c) => boot(p, c, false) },
  rogue_swift: { element: "nature", draw: (p, c) => boot(p, c, true) },
  warrior_cleave: {
    element: "steel",
    draw: (p, c) => {
      p.arc(12, 14, 9, 200, 340, c.light);
      p.arc(12, 14, 8, 200, 340, c.main);
      sword(p, c, 18, 4, 7, 17);
    },
  },
  warrior_rose_blade: {
    element: "arcane",
    draw: (p) => {
      // A sword through a pink burst.
      p.disc(12, 12, 8, "#b8327e");
      p.disc(12, 12, 6, "#ff6fbf");
      p.disc(12, 12, 3, "#ffc6e6");
      sword(p, ELEMENT.steel as Palette, 19, 3, 5, 18);
      star(p, "#ffffff", 6, 6, 2);
    },
  },
  priest_radiant_strike: {
    element: "holy",
    draw: (p, c) => {
      // A mace head bursting with light.
      p.line(5, 20, 13, 11, "#6b4a2a", 2);
      p.disc(15, 9, 4, (ELEMENT.steel as Palette).main);
      p.disc(14, 8, 1, "#ffffff");
      star(p, c.light, 20, 4, 3);
      star(p, c.light, 8, 6, 2);
    },
  },
  warrior_shield_bash: {
    element: "shield",
    draw: (p, c) => {
      shieldShape(p, c, 11, 13, 13, 15);
      star(p, "#ffe07a", 18, 6, 3);
    },
  },
  warrior_war_cry: {
    element: "rage",
    draw: (p, c) => {
      p.rect(4, 10, 4, 5, c.dark);
      p.line(8, 9, 12, 5, c.main, 2);
      p.line(8, 15, 12, 19, c.main, 2);
      p.line(12, 5, 12, 19, c.main);
      for (const r of [4, 7, 10]) p.arc(12, 12, r, -45, 45, r === 7 ? c.light : c.main);
    },
  },
  warrior_charge: {
    element: "rage",
    draw: (p, c) => {
      for (const y of [7, 12, 17]) p.line(3, y, 9, y, c.dark);
      p.line(8, 12, 19, 12, c.main, 3);
      for (let i = 0; i < 5; i++) p.line(19 - i, 12 - i, 19 - i, 12 + i, c.light);
    },
  },
  warrior_iron_skin: {
    element: "steel",
    draw: (p, c) => {
      shieldShape(p, c, 12, 12, 15, 17);
      p.rect(8, 8, 9, 2, c.light);
      for (const [x, y] of [
        [8, 13],
        [15, 13],
        [12, 17],
      ] as const)
        p.set(x, y, c.light);
    },
  },

  rogue_twin_strike: {
    element: "steel",
    draw: (p, c) => {
      dagger(p, c, 5, 4, 16, 17);
      dagger(p, c, 19, 4, 8, 17);
    },
  },
  rogue_venom_blade: {
    element: "poison",
    draw: (p, c) => {
      dagger(p, ELEMENT.steel as Palette, 5, 18, 17, 6);
      for (const [x, y] of [
        [9, 12],
        [13, 9],
        [16, 15],
      ] as const) {
        p.set(x, y, c.main);
        p.set(x, y + 1, c.main);
        p.set(x, y + 2, c.dark);
      }
    },
  },
  rogue_shadow_step: {
    element: "shadow",
    draw: (p, c) => {
      p.arc(12, 12, 8, 100, 330, c.main);
      p.arc(12, 12, 5, 130, 360, c.light);
      p.disc(13, 12, 2, c.light);
      for (const [x, y] of [
        [5, 19],
        [9, 20],
        [15, 19],
        [19, 20],
      ] as const)
        p.rect(x, y, 2, 1, c.dark);
    },
  },
  rogue_hamstring: {
    element: "poison",
    draw: (p, c) => {
      // A low cut across the leg, the blade under it and roots holding the foot.
      p.rect(9, 3, 4, 14, "#7a5a3a");
      p.rect(9, 17, 7, 3, "#5a3e24");
      p.line(4, 12, 19, 9, c.light, 2);
      dagger(p, ELEMENT.steel as Palette, 3, 21, 14, 14);
      p.arc(12, 20, 5, 200, 340, c.main);
    },
  },
  rogue_ambush: {
    element: "shadow",
    draw: (p, c) => {
      p.disc(12, 12, 8, c.dark);
      dagger(p, { ...c, main: "#dfe3ea", light: "#ffffff" }, 7, 18, 16, 7);
      star(p, c.light, 17, 6, 2);
    },
  },

  mage_firebolt: {
    element: "fire",
    draw: (p, c) => {
      // Flame tail streaking toward the lower left.
      p.line(3, 20, 11, 12, c.dark, 3);
      p.line(5, 21, 12, 14, c.main, 2);
      p.line(3, 17, 10, 11, c.main);
      p.line(7, 20, 12, 15, c.light);
      // Fireball.
      p.disc(15, 9, 6, c.dark);
      p.disc(15, 9, 5, c.main);
      p.disc(16, 8, 3, c.light);
      p.set(17, 7, "#ffffff");
    },
  },
  mage_frost_nova: {
    element: "frost",
    draw: (p, c) => {
      star(p, c.light, 12, 12, 9);
      star(p, c.main, 12, 12, 6);
      p.disc(12, 12, 2, "#ffffff");
    },
  },
  mage_blink: {
    element: "arcane",
    draw: (p, c) => {
      p.ring(9, 13, 5, c.dark);
      p.ring(15, 11, 5, c.main);
      p.ring(15, 11, 3, c.light);
      star(p, "#ffffff", 19, 5, 2);
      star(p, c.light, 5, 18, 1);
    },
  },
  mage_meteor: {
    element: "fire",
    draw: (p, c) => {
      p.line(3, 3, 12, 12, c.main, 4);
      p.line(4, 3, 12, 11, c.light, 2);
      p.disc(15, 15, 5, "#5a3a2a");
      p.disc(14, 14, 3, "#8a5a3a");
      p.set(17, 17, c.main);
      p.set(13, 17, c.main);
    },
  },

  priest_smite: {
    element: "holy",
    draw: (p, c) => {
      p.line(14, 2, 9, 11, c.main, 2);
      p.line(9, 11, 15, 11, c.main, 2);
      p.line(15, 11, 10, 21, c.main, 2);
      p.line(14, 3, 10, 10, c.light);
      p.line(3, 21, 20, 21, c.dark);
    },
  },
  // Cleanse: a drop of clear water breaking a dark swirl.
  priest_purify: {
    element: "holy",
    draw: (p, c) => {
      p.arc(12, 13, 8, 200, 520, "#7a3ad0");
      p.disc(12, 12, 5, c.main);
      p.disc(12, 8, 3, c.main);
      p.set(12, 4, c.main);
      p.disc(10, 11, 1, c.light);
      p.line(5, 19, 19, 5, c.light);
    },
  },
  rogue_shake_off: {
    element: "nature",
    draw: (p, c) => {
      p.arc(12, 12, 8, 20, 340, "#7a3ad0");
      p.rect(9, 6, 6, 12, c.main);
      p.rect(10, 7, 1, 10, c.light);
      for (const [x, y] of [
        [4, 5],
        [19, 6],
        [3, 17],
        [20, 18],
      ] as const)
        p.set(x, y, c.light);
    },
  },
  // Arcane Recall: party figures pulled toward a rune.
  mage_arcane_recall: {
    element: "arcane",
    draw: (p, c) => {
      p.arc(12, 12, 9, 0, 360, c.main);
      p.disc(12, 12, 3, c.light);
      for (const [x, y] of [
        [4, 6],
        [20, 6],
        [12, 21],
      ] as const) {
        p.disc(x, y, 2, c.main);
        p.line(x, y, 12, 12, c.dark);
      }
    },
  },
  priest_mend: {
    element: "nature",
    draw: (p, c) => {
      p.disc(8, 10, 4, c.main);
      p.disc(16, 10, 4, c.main);
      for (let i = 0; i < 8; i++) p.line(4 + i, 11 + i, 20 - i, 11 + i, c.main);
      p.disc(8, 9, 1, c.light);
      cross(p, "#ffffff", 12, 12, 3);
    },
  },
  priest_blessing: {
    element: "holy",
    draw: (p, c) => {
      p.ring(12, 6, 4, c.light);
      p.arc(12, 6, 3, 0, 360, c.main);
      for (const s of [-1, 1]) {
        for (let i = 0; i < 6; i++)
          p.line(12 + s * 2, 13 + i, 12 + s * (9 - i), 10 + i, i % 2 ? c.main : c.light);
      }
    },
  },

  // ---------------------------------------------------------------- level 25+
  warrior_sunder: {
    element: "rage",
    draw: (p, c) => {
      shieldShape(p, ELEMENT.steel as Palette, 11, 13, 13, 15);
      p.line(6, 5, 17, 21, c.main, 2);
      p.line(7, 5, 17, 20, c.light);
      p.line(5, 12, 9, 12, c.dark);
    },
  },
  warrior_execute: {
    element: "rage",
    draw: (p, c) => {
      p.line(5, 19, 17, 5, "#6b4a2a", 2);
      p.rect(13, 3, 7, 7, c.main);
      p.rect(15, 5, 5, 3, c.light);
      p.set(4, 20, c.dark);
      star(p, "#ffffff", 7, 7, 2);
    },
  },
  rogue_crippling_venom: {
    element: "poison",
    draw: (p, c) => {
      dagger(p, ELEMENT.steel as Palette, 5, 4, 16, 17);
      p.disc(17, 17, 3, c.main);
      p.set(17, 21, c.main);
      p.set(18, 22, c.dark);
      p.set(8, 20, c.light);
    },
  },
  rogue_blade_flurry: {
    element: "shadow",
    draw: (p, c) => {
      dagger(p, ELEMENT.steel as Palette, 4, 4, 13, 13);
      dagger(p, ELEMENT.steel as Palette, 20, 4, 11, 13);
      for (const r of [5, 8]) p.arc(12, 14, r, 200, 340, r === 5 ? c.light : c.main);
    },
  },
  mage_flame_wave: {
    element: "fire",
    draw: (p, c) => {
      for (const r of [6, 10, 14]) p.arc(4, 12, r, -40, 40, r === 10 ? c.light : c.main);
      flame(p, c, 5, 13, 3);
    },
  },
  mage_glacier: {
    element: "frost",
    draw: (p, c) => {
      // Ice spikes breaking up out of the ground.
      p.rect(2, 19, 20, 3, c.dark);
      for (const [x, h] of [
        [5, 8],
        [10, 14],
        [15, 11],
        [19, 7],
      ] as const)
        for (let i = 0; i < h; i++) {
          const w = Math.max(1, Math.round((h - i) / 3));
          p.line(x - w, 19 - i, x + w, 19 - i, i % 3 ? c.main : c.light);
        }
    },
  },
  mage_cataclysm: {
    element: "fire",
    draw: (p, c) => {
      p.disc(12, 13, 9, c.dark);
      p.disc(12, 13, 6, c.main);
      p.disc(12, 13, 3, c.light);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        p.set(Math.round(12 + Math.cos(a) * 11), Math.round(13 + Math.sin(a) * 10), c.light);
      }
    },
  },
  priest_prayer: {
    element: "nature",
    draw: (p, c) => {
      for (const x of [6, 12, 18]) {
        p.disc(x, 9, 2, c.light);
        p.rect(x - 2, 12, 5, 6, c.main);
      }
      cross(p, "#ffffff", 12, 3, 2);
      p.arc(12, 14, 11, 200, 340, c.light);
    },
  },
  priest_plague: {
    element: "poison",
    draw: (p, c) => {
      // A skull in a green cloud.
      p.disc(7, 15, 5, c.dark);
      p.disc(17, 15, 5, c.dark);
      p.disc(12, 11, 6, c.main);
      p.rect(9, 16, 7, 3, c.main);
      p.rect(9, 10, 2, 2, "#1a2a10");
      p.rect(14, 10, 2, 2, "#1a2a10");
      for (const x of [10, 12, 14]) p.set(x, 18, "#1a2a10");
      p.set(4, 6, c.light);
      p.set(20, 5, c.light);
    },
  },
  priest_cure_all: {
    element: "holy",
    draw: (p, c) => {
      // Three figures under one cross of light.
      cross(p, "#ffffff", 12, 6, 3);
      for (const x of [5, 12, 19]) {
        p.disc(x, 14, 2, c.light);
        p.rect(x - 2, 17, 5, 4, c.main);
      }
    },
  },
  priest_judgment: {
    element: "holy",
    draw: (p, c) => {
      p.rect(10, 2, 4, 12, c.light);
      p.rect(6, 5, 12, 3, c.light);
      p.line(3, 20, 21, 20, c.dark, 2);
      for (const r of [4, 7]) p.arc(12, 20, r, 180, 360, c.main);
    },
  },
};

// Portraits for unit frames.
const PORTRAITS: Record<string, Recipe> = {
  class_warrior: {
    element: "rage",
    draw: (p, c) => {
      shieldShape(p, ELEMENT.shield as Palette, 8, 13, 11, 13);
      sword(p, ELEMENT.steel as Palette, 19, 3, 11, 15);
      void c;
    },
  },
  class_rogue: {
    element: "shadow",
    draw: (p, c) => {
      p.arc(12, 11, 8, 180, 360, c.dark);
      p.rect(5, 11, 15, 2, c.dark);
      p.rect(9, 11, 2, 2, c.light);
      p.rect(14, 11, 2, 2, c.light);
      dagger(p, ELEMENT.steel as Palette, 5, 21, 11, 15);
      dagger(p, ELEMENT.steel as Palette, 19, 21, 13, 15);
    },
  },
  class_mage: {
    element: "arcane",
    draw: (p, c) => {
      p.line(7, 21, 15, 7, "#8a6a45", 2);
      p.disc(16, 6, 3, c.main);
      p.disc(16, 5, 1, c.light);
      star(p, "#ffffff", 6, 6, 2);
      star(p, c.light, 20, 15, 1);
    },
  },
  class_priest: {
    element: "holy",
    draw: (p, c) => {
      p.ring(12, 5, 4, c.light);
      cross(p, c.main, 12, 14, 6);
      cross(p, c.light, 12, 14, 0);
      p.rect(11, 9, 3, 12, c.main);
      p.rect(12, 9, 1, 12, c.light);
    },
  },
  mob: {
    element: "rage",
    draw: (p, c) => {
      for (const x of [7, 12, 17]) {
        p.line(x - 2, 4, x + 2, 20, c.light, 2);
        p.line(x - 1, 4, x + 2, 19, c.main);
      }
    },
  },
};

const FALLBACK: Recipe = { element: "arcane", draw: (p, c) => star(p, c.light, 12, 12, 7) };

const cache = new Map<string, string>();

/** Data URL of the icon for a skill (cached). Render with `image-rendering: pixelated`. */
export function skillIcon(skillId: string): string {
  const hit = cache.get(skillId);
  if (hit) return hit;
  const recipe = RECIPES[skillId] ?? PORTRAITS[skillId] ?? FALLBACK;
  const pal = ELEMENT[recipe.element] as Palette;
  const p = new Pix();
  recipe.draw(p, pal);

  const url = renderIcon(p, (ctx) => {
    // Element radial glow with a darker rim.
    const grad = ctx.createRadialGradient(N / 2, N / 2, 2, N / 2, N / 2, N * 0.72);
    grad.addColorStop(0, pal.bg[0]);
    grad.addColorStop(1, pal.bg[1]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, N, N);
  });
  cache.set(skillId, url);
  return url;
}

/** Unit-frame portrait: class emblem for players, claw mark for mobs. */
export function portraitIcon(kind: "player" | "mob", cls?: string): string {
  return skillIcon(kind === "mob" ? "mob" : `class_${cls ?? "warrior"}`);
}
